import { useOutletContext, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { openTunnel, closeTunnel, getActiveSessions } from '../../services/DeviceDetailsAPI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt, faPlay, faStop, faSpinner, faInfoCircle, faDownload, faTerminal } from '@fortawesome/free-solid-svg-icons';
import { Modal } from 'react-bootstrap';
import Grid from '../../components/grid/Grid';
import { formatToBrowserTime } from '../../utils/timeFormat';
import { TunnelState } from '../../utils/tunnelState';

// TEMPORARILY DISABLED (AWS SSH terminal rendering):
// We intentionally keep the SSHTerminal integration code in this file for quick restoration.
// Do not delete this import reference; uncomment together with the commented render/callback sections
// once AWS-side secure tunneling reliability issues are resolved.
// import SSHTerminal from '../../components/SSHTerminal/SSHTerminal';
import './DeviceDetails.css';


const REMOTE_ACCESS_POPUP_CONFIG = {
    terminalLaunchPath: '/ssh-terminal',
    downloadUrl: '/ortho-aws-remote-agent.zip',
    downloadFileName: 'ortho-aws-remote-agent.zip',
    instructions: [
        {
            title: 'Step 1',
            description: 'Click on Download SSH Access Package button and unzip the ortho-aws-remote-agent.zip.'
        },
        {
            title: 'Step 2',
            description: 'Navigate to unzipped ortho-aws-remote-agent folder and open command prompt inside location.'
        },
        //{
        //    title: 'Step 3',
        //    description: 'In command prompt execute command after replacing the path with ortho-aws-remote-agent entire folder \n  <strong>set SSL_CERT_FILE=C:\\Users\\Downloads\\ortho-aws-remote-agent\\AmazonRootCA1.pem </strong>'
        //},
        {
            title: 'Step 3',
            description: (
                <>
                    In command prompt execute command after replacing the path with
                    ortho-aws-remote-agent entire folder
                    {'\n'}
                    <strong>
                        set SSL_CERT_FILE=C:\Users\Downloads\ortho-aws-remote-agent\AmazonRootCA1.pem
                    </strong>
                </>
            )
        },
        {
            title: 'Step 4',
            description: (
                <>
                    Copy source token from Remote Access Instructions popup and replace
                    &lt;source-token&gt; with it and execute below in command prompt
                    {'\n'}
                    <strong>
                        ortho-aws-remote-agent.exe -t &lt;source-token&gt; -r us-east-1 -s 5555 -b 127.0.0.1 -v 5 --destination-client-type V1
                    </strong>
                    {'\n'}
                    Once above command is successful you should see message like
                    Resolved bind IP: 127.0.0.1 and Listening for new connection on port 5555.
                </>
            )
        },
        //{
        //    title: 'Step 4',
        //    description: 'Copy source token from Remote Access Instructions popup and replace <source-token> with it and execute below in command prompt \n  <strong> ortho-aws-remote-agent.exe -t <source-token> -r us-east-1 -s 5555 -b 127.0.0.1 -v 5 --destination-client-type V1 </strong> \nOnce above command is successful you should see message like Resolved bind IP: 127.0.0.1 and Listening for new connection on port 5555.'
        //},
        {
            title: 'Step 5',
            description: 'Open putty in hostname enter 127.0.0.1 and in port enter 5555. Select SSH as connection type and click on Open.'
        },
        {
            title: 'Step 6',
            description: 'It will open new putty terminal and enter login credentials.'
        }
    ]
};

const DeviceDetails = () => {
    const location = useLocation();

    const ctx = useOutletContext?.() || {};
    const qs = new URLSearchParams(location.search);
    const model = ctx?.model ?? qs.get('model') ?? '';
    const serialNumber = ctx?.serialNumber ?? qs.get('serialNumber') ?? '';

    const [activeSessions, setActiveSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openingTunnel, setOpeningTunnel] = useState(false);
    const [closingTunnel, setClosingTunnel] = useState(false);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [hasActiveSession, setHasActiveSession] = useState(false);
    const [sourceToken, setSourceToken] = useState(null);
    const [awsRegion, setAwsRegion] = useState('us-east-1');
    const [isRestoredSession, setIsRestoredSession] = useState(false);
    const [showInstructionsModal, setShowInstructionsModal] = useState(false);
    const [copyTokenStatus, setCopyTokenStatus] = useState('idle');
    const pendingTerminalTabRef = useRef(null);
    const [isSSHOpened, setIsSSHOpened] = useState(false);
    const thingName = `${model}-${serialNumber}`;

    const headers = [
        { key: 'tunnelId', label: 'Tunnel ID', width: '20%' },
        { key: 'status', label: 'Status', width: '10%' },
        { key: 'description', label: 'Description', width: '15%' },
        { key: 'createdAt', label: 'Created At', width: '18%' },
        { key: 'lastUpdatedAt', label: 'Last Updated', width: '18%' },
        { key: 'tunnelArn', label: 'Tunnel ARN', width: '19%' },
    ];

    const mapApiResponseToGridData = (dataArray) => {
        if (!dataArray || dataArray.length === 0) {
            return [];
        }

        return (dataArray ?? []).map((item, index) => ({
            id: index + 1,
            tunnelId: item?.tunnelId ?? '',
            status: item?.status ?? '',
            description: item?.description ?? '',
            createdAt: item?.createdAt
                ? formatToBrowserTime(item.createdAt, false, false)
                : '',
            lastUpdatedAt: item?.lastUpdatedAt
                ? formatToBrowserTime(item.lastUpdatedAt, false, false)
                : '',
            tunnelArn: item?.tunnelArn ?? '',
        }));
    };

    const loadActiveSessions = async () => {
        try {
            setLoading(true);

            const thingName = `${model}-${serialNumber}`;
            const response = await getActiveSessions(thingName);
            const dataArray = Array.isArray(response) ? response : [];
            const mapped = mapApiResponseToGridData(dataArray);
            setActiveSessions(mapped);
        } catch (err) {
            console.error('Failed to load active sessions:', err);
            setActiveSessions([]);
        } finally {
            setLoading(false);
        }
    };

    const preOpenTerminalTab = () => {
        if (pendingTerminalTabRef.current && !pendingTerminalTabRef.current.closed) {
            return pendingTerminalTabRef.current;
        }

        pendingTerminalTabRef.current = window.open('', '_blank');
        if (!pendingTerminalTabRef.current) {
            toast.warning('Please allow pop-ups to open the SSH Terminal tab.');
            return null;
        }
        if (pendingTerminalTabRef.current && !pendingTerminalTabRef.current.closed) {
            pendingTerminalTabRef.current.document.title = 'Launching SSH Terminal...';
            pendingTerminalTabRef.current.document.body.innerHTML = '<div style="font-family:Arial,sans-serif;padding:24px;color:#1f2937;">Launching SSH Terminal...</div>';
        }
        return pendingTerminalTabRef.current;
    };

    const launchSshTerminalInNewTab = ({ tunnelId, sourceToken, region, sessionId }) => {
        const terminalUrl = new URL(REMOTE_ACCESS_POPUP_CONFIG.terminalLaunchPath, window.location.origin);
        terminalUrl.searchParams.set('thingName', thingName);
        terminalUrl.searchParams.set('tunnelId', tunnelId);
        terminalUrl.searchParams.set('sourceToken', sourceToken);
        terminalUrl.searchParams.set('region', region);
        terminalUrl.searchParams.set('sessionId', sessionId);

        const pendingTab = pendingTerminalTabRef.current;

        if (pendingTab && !pendingTab.closed) {
            pendingTab.location.replace(terminalUrl.toString());
            pendingTerminalTabRef.current = null;
            return;
        }

        window.open(terminalUrl.toString(), '_blank', 'noopener,noreferrer');
    };

    const handleStartSession = async () => {
        if (!model || !serialNumber) {
            toast.error('Missing model or serial number.');
            return;
        }

        try {
            setOpeningTunnel(true);
            const response = await openTunnel({ thingName });

            const sourceToken = response?.sourceAccessToken || response?.sourceToken;

            if (response?.tunnelId && sourceToken) {
                let region = awsRegion;
                if (response?.tunnelArn) {
                    const arnParts = response.tunnelArn.split(':');
                    if (arnParts.length >= 4) {
                        region = arnParts[3];
                    }
                }

                const sessionId = response?.sessionId;

                if (!sessionId) {
                    toast.error('Tunnel started, but the relay session ID was not returned.');
                    return;
                }

                const result = TunnelState.setActiveTunnel(
                    thingName,
                    response.tunnelId,
                    sourceToken,
                    region,
                    sessionId
                );

                if (!result.success) {
                    toast.error(result.message);
                    return;
                }

                setCurrentSessionId(response.tunnelId);
                setSourceToken(sourceToken);
                setAwsRegion(region);
                setHasActiveSession(true);
                setIsRestoredSession(false);

                toast.success(`Session started for ${thingName}!`);
                await loadActiveSessions();
            } else {
                if (pendingTerminalTabRef.current && !pendingTerminalTabRef.current.closed) {
                    pendingTerminalTabRef.current.close();
                    pendingTerminalTabRef.current = null;
                }
                toast.error('Invalid response from server. Missing tunnelId or sourceAccessToken.');
            }
        } catch (err) {
            if (pendingTerminalTabRef.current && !pendingTerminalTabRef.current.closed) {
                pendingTerminalTabRef.current.close();
                pendingTerminalTabRef.current = null;
            }
            console.error('Failed to start session:', err);
            toast.error('Failed to start session. Please try again.');
        } finally {
            setOpeningTunnel(false);
        }
    };

    const handleOpenSSH = async () => {
        if (isSSHOpened) return;

        const existingTunnel = TunnelState.getActiveTunnel();

        if (!existingTunnel || !existingTunnel.sessionId) {
            toast.error(
                '[SSH] No active tunnel session found or tunnel might be already used. A new tunnel session is required.'
            );

            // Replace this with your existing tunnel creation/initialization flow.
            // Do not continue building the terminal URL without a valid sessionId.
            return;
        }


        if (existingTunnel && existingTunnel.thingName !== thingName) {
            toast.error(
                `Cannot start tunnel.Another tunnel is active for device: ${existingTunnel.thingName}. Please end that session first.`
            );
            return;
        }

        const sessionId = existingTunnel.sessionId;

        if (!sessionId) {
            toast.error(
                'Relay session ID is missing. Please close the current tunnel and start a new session.'
            );
            terminalTab.close();
            return;
        }

        const terminalTab = window.open(
            "about:blank",
            "_blank"
        );

        if (!terminalTab) {
            toast.warning("Please allow pop-ups to open the SSH Terminal tab.");
            return;
        }

        try {
            terminalTab.document.title = "Launching SSH Terminal...";
            terminalTab.document.body.innerHTML =
                '<div style="font-family:Arial,sans-serif;padding:24px;">Launching SSH Terminal...</div>';

            const terminalUrl = new URL(
                REMOTE_ACCESS_POPUP_CONFIG.terminalLaunchPath,
                window.location.origin
            );

            terminalUrl.searchParams.set("thingName", thingName);
            terminalUrl.searchParams.set("tunnelId", currentSessionId);
            terminalUrl.searchParams.set("sourceToken", sourceToken);
            terminalUrl.searchParams.set("region", awsRegion);
            terminalUrl.searchParams.set("sessionId", sessionId);

            terminalTab.location.replace(terminalUrl.toString());

            setIsSSHOpened(true);
            toast.success(`SSH Terminal opened for ${thingName}.`);
        } catch (error) {
            if (!terminalTab.closed) {
                terminalTab.close();
            }

            console.error("Failed to open SSH Terminal:", error);
            toast.error("Failed to open SSH Terminal.");
        }
    };

    const handleEndSession = async () => {
        if (!currentSessionId) {
            toast.error('No active session found to end.');
            return;
        }

        try {
            setClosingTunnel(true);
            await closeTunnel({
                thingName,
                tunnelId: currentSessionId
            });

            TunnelState.clearActiveTunnel(thingName);

            setCurrentSessionId(null);
            setSourceToken(null);
            setHasActiveSession(false);
            setIsRestoredSession(false);
            setShowInstructionsModal(false);
            setCopyTokenStatus('idle');
            setIsSSHOpened(false);
            toast.success(`Session ended for ${thingName}!`);

            await loadActiveSessions();
        } catch (err) {
            console.error('Failed to end session:', err);
            toast.error('Failed to end session. Please try again.');
        } finally {
            setClosingTunnel(false);
        }
    };

    const handleRefresh = () => {
        loadActiveSessions();
    };

    const handleCopyToken = async () => {
        if (!sourceToken) {
            toast.error('No source token available to copy.');
            return;
        }

        try {
            await navigator.clipboard.writeText(sourceToken);
            setCopyTokenStatus('copied');
            toast.success('Token copied');

            setTimeout(() => {
                setCopyTokenStatus('idle');
            }, 2000);
        } catch (error) {
            console.error('Failed to copy source token:', error);
            toast.error('Unable to copy token. Please copy manually.');
        }
    };

    // TEMPORARILY DISABLED (terminal lifecycle callbacks):
    // These callbacks are intentionally preserved for the embedded SSHTerminal component so
    // terminal close/expiry handling can be restored without redesign. Uncomment together with
    // the SSHTerminal render block below when AWS tunnel reliability is fixed.
    // const handleConnectionClose = () => {
    //     setIsRestoredSession(false);
    //     toast.info('SSH connection closed');
    // };

    // const handleTokenExpired = () => {
    //     setIsRestoredSession(true);
    //     toast.error('Connection token has expired. Please end this session and start a new one.');
    // };

    useEffect(() => {
        if (!model || !serialNumber) {
            toast.error('Missing model or serial number.');
            return;
        }

        const initializeSession = async () => {
            await loadActiveSessions();

            const activeTunnel = TunnelState.getActiveTunnel();

            if (activeTunnel) {
                if (activeTunnel.thingName === thingName) {
                    try {
                        const sessions = await getActiveSessions(thingName);
                        const activeTunnelOnBackend = sessions?.find(
                            session => session.tunnelId === activeTunnel.tunnelId && session.status === 'OPEN'
                        );

                        if (activeTunnelOnBackend) {
                            setCurrentSessionId(activeTunnel.tunnelId);
                            setSourceToken(activeTunnel.sourceToken);
                            setAwsRegion(activeTunnel.region);
                            setHasActiveSession(true);
                            setIsRestoredSession(false);
                        } else {
                            TunnelState.clearActiveTunnel();
                            setCurrentSessionId(null);
                            setSourceToken(null);
                            setHasActiveSession(false);
                            setIsRestoredSession(false);
                            toast.info('Previous tunnel session has expired');
                        }
                    } catch (error) {
                        console.error('Error verifying tunnel:', error);
                        setCurrentSessionId(activeTunnel.tunnelId);
                        setSourceToken(activeTunnel.sourceToken);
                        setAwsRegion(activeTunnel.region);
                        setHasActiveSession(true);
                        setIsRestoredSession(false);
                    }
                } else {
                    setCurrentSessionId(null);
                    setSourceToken(null);
                    setHasActiveSession(false);
                    toast.warning(`Another tunnel is active for device: ${activeTunnel.thingName}`);
                }
            } else {
                try {
                    const sessions = await getActiveSessions(thingName);
                    const openTunnelOnBackend = sessions?.find(s => s.status === 'OPEN');

                    if (openTunnelOnBackend) {
                        setCurrentSessionId(openTunnelOnBackend.tunnelId);
                        setHasActiveSession(true);
                        setIsRestoredSession(true);
                        toast.warning('An active tunnel exists from this browser. Please end the session and start a new one.');
                    } else {
                        setCurrentSessionId(null);
                        setSourceToken(null);
                        setHasActiveSession(false);
                    }
                } catch (error) {
                    console.error('Error checking backend sessions:', error);
                }
            }
        };

        initializeSession();

    }, [model, serialNumber, thingName]);

    const isProcessing = openingTunnel || closingTunnel;

    useEffect(() => {
        const channel = new BroadcastChannel('ssh-terminal-channel');

        const handleMessage = (event) => {
            if (event.data?.type === 'SSH_TERMINAL_CLOSED') {
                setIsSSHOpened(false);
            }
        };

        channel.addEventListener('message', handleMessage);

        const handleStorage = (event) => {
            if (event.key === 'ssh-terminal-closed') {
                setIsSSHOpened(false);
            }
        };

        window.addEventListener('storage', handleStorage);

        return () => {
            channel.removeEventListener('message', handleMessage);
            channel.close();
            window.removeEventListener('storage', handleStorage);
        };
    }, []);

    return (
        <div className="rs-page-wrapper">
            <div className="rs-header">
                <div className="rs-header-buttons">
                    <button
                        type="button"
                        onClick={handleStartSession}
                        className="btn btn-primary"
                        disabled={isProcessing || hasActiveSession}
                    >
                        {openingTunnel ? (
                            <>
                                <FontAwesomeIcon icon={faPlay} className="me-1" />
                                Starting Session...
                                <FontAwesomeIcon icon={faSpinner} spin className="ms-2" />
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faPlay} className="me-1" />
                                Start Session
                            </>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={handleOpenSSH}
                        className="btn btn-primary"
                        disabled={isProcessing || !hasActiveSession || isSSHOpened}
                    >
                        <FontAwesomeIcon icon={faTerminal} className="me-1" />
                        {isSSHOpened ? "SSH Opened" : "Open SSH"}
                    </button>

                    <button
                        type="button"
                        onClick={handleEndSession}
                        className="btn btn-danger"
                        disabled={isProcessing || !hasActiveSession}
                    >
                        <FontAwesomeIcon icon={faStop} className="me-1" />
                        {closingTunnel ? "Ending Session..." : "End Session"}
                    </button>

                    <button
                        type="button"
                        onClick={() => setShowInstructionsModal(true)}
                        className="btn btn-primary"
                    >
                        <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                        Remote Access Instructions
                    </button>
                </div>

                {/*{hasActiveSession && isRestoredSession && (*/}
                {/*    <div className="alert alert-warning mt-3" role="alert">*/}
                {/*        <strong>⚠️ Active Session Detected:</strong> A tunnel session exists*/}
                {/*        for this device, but{" "}*/}
                {/*        {sourceToken*/}
                {/*            ? "the connection token has expired"*/}
                {/*            : "you cannot access it from this browser"}.*/}
                {/*        Please end the current session and start a new one to connect.*/}
                {/*    </div>*/}
                {/*)}*/}
                <button
                    type="button"
                    onClick={handleRefresh}
                    className="btn btn-primary refresh-button"
                    disabled={isProcessing}
                >
                    <FontAwesomeIcon icon={faSyncAlt} className="me-1" />
                    Refresh
                </button>
            </div>

            {/*
                                TEMPORARILY DISABLED (embedded AWS SSH terminal mount point)

                                Purpose:
                                - This block renders the embedded SSHTerminal UI, initializes tunnel/stream loading,
                                    and displays live SSH output.

                                Why disabled:
                                - AWS/infrastructure-side secure tunneling reliability issues are currently causing an
                                    inconsistent terminal experience.

                                Preservation intent:
                                - The implementation is intentionally retained and commented out (not deleted) so the
                                    team can re-enable with minimal code changes once AWS-side issues are resolved.

                                Restore guidance:
                                - Uncomment this block.
                                - Uncomment the SSHTerminal import near the top of this file.
                                - Uncomment handleConnectionClose and handleTokenExpired callbacks above.
                        */}
            {/*
                        <SSHTerminal
                                sourceToken={sourceToken}
                                region={awsRegion}
                                thingName={thingName}
                                onConnectionClose={handleConnectionClose}
                                onTokenExpired={handleTokenExpired}
                        />
                        */}

            <div className="rs-content">
                <Grid
                    headers={headers}
                    data={activeSessions}
                    rowsPerPage={50}
                    canMultiSelect={false}
                    showPagination={true}
                    loading={loading}
                    sortable={true}
                />
            </div>

            <Modal
                show={showInstructionsModal}
                onHide={() => setShowInstructionsModal(false)}
                centered
                dialogClassName="ssh-instructions-modal-dialog"
                contentClassName="ssh-instructions-modal-content"
                backdrop
                keyboard
            >
                <Modal.Header closeButton>
                    <Modal.Title>Remote Access Instructions</Modal.Title>
                </Modal.Header>
                <Modal.Body className="ssh-instructions-modal-body">
                    <div className="ssh-instructions-intro">
                        This temporary workflow replaces the embedded terminal while AWS-side reliability issues are being addressed.
                    </div>

                    <div className="ssh-token-section">
                        <h6>Source Token</h6>
                        <div className="ssh-token-box">
                            {sourceToken || 'Source token is not available for this browser session.'}
                        </div>
                        <div className="ssh-token-actions">
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={handleCopyToken}
                                disabled={!sourceToken}
                            >
                                {copyTokenStatus === 'copied' ? 'Copied!' : 'Copy Token'}
                            </button>
                        </div>
                    </div>

                    <hr className="ssh-instructions-divider" />

                    <h6 className="ssh-instructions-title">Instructions</h6>

                    <div className="ssh-instructions-steps" >
                        {REMOTE_ACCESS_POPUP_CONFIG.instructions.map((step) => (
                            <div key={step.title} className="ssh-instructions-step">
                                <h6>{step.title}</h6>
                                <p style={{ whiteSpace: "pre-wrap" }}>
                                    {step.description}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="ssh-instructions-actions">
                        <a
                            href={REMOTE_ACCESS_POPUP_CONFIG.downloadUrl}
                            className="btn btn-primary"
                            download={REMOTE_ACCESS_POPUP_CONFIG.downloadFileName}
                        >
                            <FontAwesomeIcon icon={faDownload} className="me-1" />
                            Download SSH Access Package
                        </a>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default DeviceDetails;
